import {compressPrintOptions, getLinoOptionBase, isUVPrintOption, offeredForInstitution} from './print-options';
import {logger} from '../logger';
import {observable, action, computed, makeObservable} from 'mobx';
import {pricing} from './api';
import {ProductUI, StoreInfo, ViewProduct, ProductState, GlassPrices, ViewGlass, ProductStateShare, ProductDocSize, LinoCustomization, InsigniaCustomization} from './i';
import {ga4_item} from '../checkout/i';
import {DefaultPCS, EP_DEFAULT_FIXED_SIZE, EPBorderType, EPMethod, EP_SNAP_SIZES, EPSizeType, ProductCustomizationState} from './rendering/render.def';
import {epLineFailsRegex, epLineSpec, mapPlateTemplatesToOptions, parseEPPlate2} from './components/engraved-plates/ep-template/ep-templates';
import {clearTemplate, restoreTemplate, setTemplatePlateSize} from './components/engraved-plates/ep-template/ep-template-apply';
import {INST_PLATE_DEFAULT_SIZE, isInstPlateTemplateSet, plateFinishFor, restoreFromShare} from './components/engraved-plates/inst-plate/inst-plate-rules';
import {r} from './rendering/core';
import * as DocSizes from './components/doc-sizes-and-categories/doc-size-pulse';
import {applySharedDocumentSizing, restoreFixedDisplaySizing} from './components/doc-sizes-and-categories/document-sizing';
import {prerender_set_pcs} from './rendering/web-only-render';
import {applyCafOptionOverrides} from './caf/caf-option-overrides';
import {updateShippingBoxID} from './calc';
import {toJS} from 'mobx';
import {RenderV2Object} from '../renderV2/frame-builder/render-v2-d';
import {buildShareUrl, decodeShareStateFromUrl} from './components/share-state/share-state';
import {currency_noNegatives, debounce, getParameterByName, isNullOrWhiteSpace, modal, Portal, RenderType} from '../util';
import {categoryIdsFromProduct, createDefaultProductGlass, resetProductInitDerivedFields} from './product-init/product-init-state';
import {defaultUVOptions, parseUVOptions, uvContentJSON} from './components/uv-print/uv-template';
import {optionsForStore} from './store-options';

declare var __isMyFrames: boolean;
declare var __StoreID: number;
declare var __preload_product: string;
declare var __preload_glass_prices: string;
declare var __fast_ship: boolean;
declare var klaviyo: any[];
declare const window: any;
declare const marketing_product_visit: (arg: any[]) => void;
declare var __company_name: string;
declare var __domain_name: string;
declare var __oversized: number[];
declare var __is_production: string;

declare var __oid_m0: number;
declare var __oid_m1: number;
declare var __oid_m2: number;

// instIds and gradSchoolIds arrive as PARALLEL slot lists (dbo.Entities holds ten
// InstitutionID_n + GradSchoolID_n pairs), so slot ix of one belongs with slot ix of the other -
// this keeps only the grad schools assigned to this store's institution.
// If the two lists are not the same length the pairing has been lost upstream (older cached
// products were de-duplicated per-array, which collapsed them to different lengths); pairing by
// index would then silently drop valid grad schools, so keep them all instead.
const gradSchoolIdsForInstitution = (instIds: number[], gradSchoolIds: number[], institutionID: number): number[] => {
    const _instIds = instIds ?? [];
    const _gradSchoolIds = gradSchoolIds ?? [];
    const _paired = _instIds.length === _gradSchoolIds.length ? _gradSchoolIds.filter((_, ix) => _instIds[ix] === institutionID) : _gradSchoolIds;
    return Array.from(new Set(_paired));
};

const createDefaultProductUI = (): ProductUI => ({
    product_title: '',
    item_id: '',
    sku: '',
    msrp: 0,
    price: 0,
    price_discount: 0,
    price_summary: null,
    price_resolved: false,
    price_seeded: false,
    pricing_loading: false,
    pricing_error: '',
    review_count: 0,
    review_score: 5,
    doc_type: '',
    box_id: -1,
    categoryIds: [],

    show_grad_year: true,
    grad_year_input: '',
    show_atc: true,
    atc_busy: false,
    show_cust: true,
    show_pricing: true,
    is_uv_admin: false,
    is_ep_admin: false,

    name_moulding: '',
    moulding_has_zoom: true,
    moulding_zoom_path: '',
    moulding_zoom_path_th: '',

    name_mat1: '',
    name_mat2: '',
    name_glass: '',
    name_insignia: '',

    title_mat_combo: '',
    title_moulding: '',
    title_mat1: '',
    title_mat2: '',
    title_glass: '',
    title_insignia: '',
    title_vlbg: '',

    color_mat1: '',
    color_mat2: '',
    old_style_composite: '',

    linen: false,

    doRender: false,
    update_pricing: false,
    update_doc_sizes: false,

    zoom_thumbnails: false,
    zoom_real_images: false,
    show_caf_doc_sizes: false,
    show_caf_doc_sizes_both: false,

    bo_msg: '',
    is_bo: false,

    moulding_th5: false,
    moulding_th6: false,

    image_uploaded: false,
    image_uploaded_msg: '',
    image_uploading: false,
    image_upload_error: false,
    upload_fee: 0,
    upload_fee_applied: false,
    processing_title: '',
    processing_text: '',
    allow_customer_photos: true,
    allow_reviews: true,
    allow_smartbox: true,
    ep_mode_expanded: false,
    ep_overflowing: false,
    ep_template_show_errors: false,
    fast_ship_now: false,
});

export class ProdStore {
    @observable ui: ProductUI;
    @observable ps: ProductState;
    @observable pcs!: ProductCustomizationState;
    @observable prod!: ViewProduct;
    @observable si!: StoreInfo;
    @observable isRetail: boolean = true;
    @observable debug: boolean = true;
    @observable errorLog: string = '';
    @observable errorLogInfo: string = '';
    @observable apiError: boolean = false;
    @observable ready: boolean = false;
    @observable __fast_ship_orig: boolean = false;
    @observable __fast_ship_warned: boolean = false;
    /** The product page redesign (B) shows the Fast Ship change in its ship line, so pulse_fast_ship opens no "Fast Shipping Removed" notice. */
    @observable fast_ship_notice_inline: boolean = false;
    @observable selected_doc_size: number = 3;
    @observable glass_prices: GlassPrices[] = [];
    @observable glass: ViewGlass[];
    @observable ppi_w: number = -1;
    @observable ppi_h: number = -1;
    @observable ppi_w_max: number = -1;
    @observable ppi_h_max: number = -1;
    @observable ppi_w_actual: number = -1;
    @observable ppi_h_actual: number = -1;
    @observable product_thumbnail_height: number = 75;
    @observable IsPortal3: boolean = false;
    @observable currentGradSchoolID: number = -1;
    @observable hasDYFLinoNonProduct: boolean = false;
    @observable hasSomeProductLino: boolean = false;
    @observable hasOnlyOneProductLino: boolean = false;
    @observable RenderingV2: boolean = false;
    @observable frameV2!: RenderV2Object;
    @observable debug_lino: boolean = false;

    // Preserve cached parts for saved configurations, pricing and rendering.
    // Only the choices displayed to shoppers are compressed.
    @computed get linoChoices() {
        const linos = this.prod?.linos ?? [];
        const base = linos.find(lino => lino.id === this.prod?.LinoID);
        // Grad-school artwork follows the product's print method. CAF and Icon
        // products retain their UV preference, as do products with no base lino.
        const preferUV = !base || !!this.prod.IsCAF || [83, 84, 87, 91, 92].includes(this.prod.ProductTypeID) || isUVPrintOption(base);
        return compressPrintOptions(linos.filter(this.linoAllowedOnMat), 'lino', preferUV);
    }

    @computed get insigniaChoices() {
        return compressPrintOptions(this.prod?.insignias ?? [], 'insignia');
    }

    preferredLinoId = (id: number) => this.linoChoices.preferredIds.get(id) ?? id;
    preferredInsigniaId = (id: number) => this.insigniaChoices.preferredIds.get(id) ?? id;

    // The store's linos (store-options.ts), each with the grad schools of the institution it was kept for: the store's, or for a
    // cross-listed product the institution its linos share.
    storeLinos = (linos: LinoCustomization[]): LinoCustomization[] => {
        const storeInst = this.si.InstitutionID;
        return optionsForStore(linos, storeInst).map(lino => ({
            ...lino,
            gradSchoolIds: gradSchoolIdsForInstitution(lino.instIds, lino.gradSchoolIds, lino.instIds?.includes(storeInst) ? storeInst : lino.instIds[0]),
        }));
    };

    // The institution of the seals the store offers besides the product's own: the store's, or the one a cross-listed product's
    // seals share. insigniaLinoChanged (selection/insignia.ts) re-picks a seal among those linked to it, in any of the ten slots.
    insigniaInstitutionID = -1;

    // The store's insignias: the product's own and those linked to the store's institution (offeredForInstitution), and for a
    // cross-listed product its own seals (store-options.ts), each with the grad schools of the institution it was kept for.
    storeInsignias = (insignias: InsigniaCustomization[]): InsigniaCustomization[] => {
        const storeInst = this.si.InstitutionID;
        const own = this.prod.InsigniaID;
        const others = optionsForStore(
            insignias.filter(insignia => !(own > 0 && insignia.id === own)),
            storeInst,
        );
        this.insigniaInstitutionID = others[0] && !others[0].instIds?.includes(storeInst) ? others[0].instIds[0] : storeInst;
        return insignias
            .filter(insignia => offeredForInstitution(insignia, storeInst, own) || others.includes(insignia))
            .map(insignia => ({
                ...insignia,
                gradSchoolIds: gradSchoolIdsForInstitution(insignia.instIds, insignia.gradSchoolIds, others.includes(insignia) ? this.insigniaInstitutionID : storeInst),
            }));
    };

    @observable docsizes_available: ProductDocSize[] = [];
    @observable docsizes_has_different_names: boolean = false;
    @observable docsizes_name: string = '';
    @observable docsizes_multiple_active: boolean = false;
    @observable docsizes_enabled: boolean = false;
    @observable docsizes_selected_id: number = -1;
    @observable docsizes_orig_id: number = -1;

    @observable is_production: boolean = true;

    private onWindowResize = debounce(() => this.pulse(true), 150);
    private resizeListenerAttached = false;

    constructor() {
        this.ui = createDefaultProductUI();
        this.glass = createDefaultProductGlass();
        this.ps = {} as ProductState;
        makeObservable(this);
    }

    @action
    resetInitState = () => {
        resetProductInitDerivedFields(this);
        this.ui = createDefaultProductUI();
        this.glass = createDefaultProductGlass();
    };

    @action
    init = () => {
        this.is_production = __is_production === '1';
        this.debug = !this.is_production;
        this.debug_lino = this.debug_lino && !this.is_production;

        this.IsPortal3 = Portal?.PortalID === 3;
        this.__fast_ship_orig = __fast_ship;
        this.isRetail = !__isMyFrames;
        this.prod = JSON.parse(JSON.stringify(__preload_product)) as ViewProduct;
        this.glass_prices = JSON.parse(JSON.stringify(__preload_glass_prices)) as GlassPrices[];
        this.si = this.prod.StoreInfo[__StoreID];
        this.currentGradSchoolID = this.si.GradSchoolID;

        if (this.prod.ProductID === 51558) {
            __fast_ship = true;
            this.__fast_ship_orig = true;
        }

        if (this.prod.mouldings) {
            this.prod.mouldings = this.prod.mouldings.filter(x => x.storeId == __StoreID);
            this.prod.mouldings.map((m, i) => (m.i = i));

            const _fm = this.prod.mouldings.filter(x => x.id === this.prod.MouldingID);
            if (_fm && _fm.length > 0) {
                s.ui.moulding_th5 = _fm[0].th5;
                s.ui.moulding_th6 = _fm[0].th6;
            }
        } else {
            s.prod.mouldings = [];
        }

        if (this.prod.mats) {
            this.prod.mats = this.prod.mats.filter(x => x.storeId == __StoreID);
            this.prod.mats.map((m, i) => (m.i = i));
        } else {
            s.prod.mats = [];
        }
        if (this.prod.caf_mat1) {
            this.prod.caf_mat1 = this.prod.caf_mat1.filter(x => x.storeId == __StoreID);
            this.prod.caf_mat1.filter(x => x.storeId == __StoreID).map((m, i) => (m.i = i));
        } else {
            s.prod.caf_mat1 = [];
        }
        if (this.prod.caf_mat2) {
            this.prod.caf_mat2 = this.prod.caf_mat2.filter(x => x.storeId == __StoreID);
            this.prod.caf_mat2.filter(x => x.storeId == __StoreID).map((m, i) => (m.i = i));
        } else {
            s.prod.caf_mat2 = [];
        }

        if (!this.prod.IsCAF) {
            if (this.prod.insignias) {
                for (let i = 0; i < this.prod.insignias.length; i++) {
                    if (!this.prod.insignias[i].gradSchoolIds) this.prod.insignias[i].gradSchoolIds = [];
                }

                this.prod.insignias = this.prod.insignias.filter(x => x.storeId == __StoreID);
                this.prod.insignias.filter(x => x.storeId == __StoreID).map((m, i) => (m.i = i));

                this.prod.insignias = this.storeInsignias(this.prod.insignias);
            } else {
                s.prod.insignias = [];
            }
        }

        // caf overrides
        if (s.prod.IsCAF) {
            applyCafOptionOverrides(s.prod, {oid_m0: __oid_m0, oid_m1: __oid_m1, oid_m2: __oid_m2});
        }

        if (s.prod.CustomizationMethod < 2 && s.prod.ForcePlexiglass) {
            s.ps.use_plexi = true;
        }

        if (this.debug_lino && this.prod.linos) logger.log('store linos', toJS(this.prod.linos));

        if (this.prod.linos) {
            this.prod.linos = this.prod.linos.filter(x => x.storeId == __StoreID);
            this.prod.linos.map((m, i) => (m.i = i));

            for (let i = 0; i < this.prod.linos.length; i++) {
                if (!this.prod.linos[i].gradSchoolIds) this.prod.linos[i].gradSchoolIds = [];
            }

            var _checkTest = this.prod.linos.filter(x => x.id == -5);
            var _checkTestNonDYF = this.prod.linos;
            s.hasDYFLinoNonProduct = _checkTest && _checkTest.length > 0;
            s.hasSomeProductLino = _checkTestNonDYF && _checkTestNonDYF.length > 0;
            s.hasOnlyOneProductLino = _checkTestNonDYF && _checkTestNonDYF.length === 1;

            if (!this.prod.IsCAF) {
                if (this.si.auto_grad_lino) {
                    for (let i = 0; i < this.prod.linos.length; i++) {
                        this.prod.linos[i].fpc_id = this.si.auto_grad_lino_fpc;
                        this.prod.linos[i].fpc_name = 'Graduate Schools';
                    }
                }

                if (__isMyFrames && this.prod.LinoID > 0) {
                    this.prod.linos = this.prod.linos.filter(x => x.id == this.prod.LinoID);
                    if (this.debug_lino) logger.debug('prod lino set to one due to myframes');

                    // no base lino, but some product or dyf lino:
                } else if (this.prod.linos.length > 1 && (s.hasSomeProductLino || s.hasDYFLinoNonProduct) && this.prod.LinoID < 1) {
                    // do nothing except check for inst and gs ids
                    if (s.hasSomeProductLino) {
                        this.prod.linos = this.storeLinos(this.prod.linos);

                        if (this.debug_lino) logger.debug('lino prod', this.prod.linos.length);
                    }
                } else if ((!this.prod.LinoID || this.prod.LinoID < 1) && !s.hasSomeProductLino && !s.hasDYFLinoNonProduct) {
                    s.prod.linos = [];
                    if (this.debug_lino) logger.debug('prod linos empty due to no base prod lino and no dyf linos and no prod linos');
                } else if (this.prod.linos.length > 1 && _checkTestNonDYF.length > 1) {
                    this.prod.linos = this.storeLinos(this.prod.linos);

                    if (this.debug_lino) logger.debug('lino prod', this.prod.linos.length);
                }
            }
        } else {
            s.prod.linos = [];
            if (this.debug_lino) logger.debug('prod linos empty');
        }

        if (s.prod.CustomizationMethod === 2 && !s.prod.IsCAF) {
            let _hit = false;
            if (this.si.DocumentID > 0) {
                _hit = true;
                s.prod.DocumentID = this.si.DocumentID;
                s.prod.orig_pcs.DocumentID = this.si.DocumentID;
                s.prod.DocumentName = this.si.DocumentName;
                s.prod.DocumentDisplayName = this.si.DocumentDisplayName;
                s.prod.orig_pcs.Base_DocumentName = this.si.DocumentName;
            }
            if (this.si.DocumentID2 > 0) {
                _hit = true;
                s.prod.DocumentID2 = this.si.DocumentID2;
                s.prod.orig_pcs.DocumentID2 = this.si.DocumentID2;
                s.prod.DocumentName2 = this.si.DocumentName2;
                s.prod.DocumentDisplayName2 = this.si.DocumentDisplayName2;
                s.prod.orig_pcs.Base_DocumentName2 = this.si.DocumentName2;
            }

            if (_hit) {
                if (!isNullOrWhiteSpace(this.si.th_auto_2_1)) {
                    s.prod.th_auto_2_1 = this.si.th_auto_2_1;
                    s.prod.th6_special = true;
                }
                if (!isNullOrWhiteSpace(this.si.th_auto_2_2)) {
                    s.prod.th_auto_2_2 = this.si.th_auto_2_2;
                    s.prod.th5_special = true;
                }
                if (!isNullOrWhiteSpace(this.si.th_auto_2_16)) {
                    s.prod.th_auto_2_16 = this.si.th_auto_2_16;
                }
                if (!isNullOrWhiteSpace(this.si.th_auto_2_17)) {
                    s.prod.th_auto_2_17 = this.si.th_auto_2_17;
                }
            }
        }

        // setup ui
        this.ui.item_id = this.prod.ItemID;
        this.ui.price = this.isRetail ? this.si.Price_Retail : this.si.Price_Wholesale;
        this.ui.msrp = this.si.priceMSRPDec;
        // si.ProductName: the store's portal wording (customframes / militaryframes never say "diploma")
        this.ui.product_title = !isNullOrWhiteSpace(this.si.ProductName) ? this.si.ProductName : this.prod.Name;
        this.ui.doc_type = this.prod.DocType;
        this.ui.box_id = this.prod.Shipping_BoxID;
        this.ui.categoryIds = categoryIdsFromProduct(this.prod.categories);
        if (this.si.StoreTypeID === 5 && this.isRetail) {
            this.ui.show_grad_year = false;
        }
        if (this.si.StoreTypeID === 5 && this.isRetail) {
            this.ui.show_cust = false;
        }

        if (this.prod.ForcePlexiglass) {
            this.ps.use_plexi = true;
        }
        this.ps.is_caf = this.prod.IsCAF;
        this.prod.orig_pcs.IsCAF = this.prod.IsCAF;
        this.ps.customized_core = false;
        this.ps.customized_glass = false;
        this.ps.base_product_id = this.prod.ProductID;
        this.ps.base_item_id = this.prod.ItemID;
        this.ps.docWidth = this.prod.docWidth;
        this.ps.docHeight = this.prod.docHeight;
        this.ps.docWidth2 = this.prod.docWidth2;
        this.ps.docHeight2 = this.prod.docHeight2;
        this.ps.moulding_id = this.prod.MouldingID;
        this.ps.moulding_extWidth = this.prod.MouldingExtWidth;
        this.ui.moulding_has_zoom = this.prod.MouldingHasZoom;
        this.ui.moulding_zoom_path = this.prod.Moulding_IMG_Zoom;
        this.ui.moulding_zoom_path_th = this.prod.Moulding_IMG_ZoomThumb;
        this.ps.matcombo_id = this.prod.MatComboID;
        this.ps.mat1_id = this.prod.Mat1_ID;
        this.ps.mat2_id = this.prod.Mat2_ID;
        this.ps.insignia_id = this.prod.InsigniaID;
        this.ps.insignia_base_name = this.prod.InsigniaName;
        this.ui.name_insignia = this.prod.InsigniaName ? this.prod.InsigniaName.toLowerCase() : '';
        this.ps.glass_id = 1;
        this.ps.ep_id = 0;
        this.ps.ep_id_view = 37;
        this.ps.wholesale = __isMyFrames;
        this.ps.qty = 1;
        this.ui.review_count = this.prod.reviewCount;
        this.ui.review_score = this.prod.reviewScore;
        if (!this.ps.is_caf) {
            this.ps.item_id = this.prod.orig_pcs.ItemID;
        } else {
            s.ui.moulding_has_zoom = true;
        }
        this.ps.category_map_id = this.prod.orig_pcs.SelectedCategoryMapID;

        if (this.prod.EngravedPlateStatusID === 1) {
            this.ps.ep_id = 37;
        } else if (this.prod.EngravedPlateStatusID === 2) {
            this.ps.ep_id = 39;
            this.ps.ep_id_view = 39;
        } else if (this.prod.EngravedPlateStatusID === 4) {
            this.ps.ep_id_view = 39;
        } else if (this.prod.EngravedPlateStatusID === 6) {
            this.ps.ep_id = 1099;
            this.ps.ep_id_view = 1099;
        } else if (this.prod.EngravedPlateStatusID === 7) {
            this.ps.ep_id_view = 1099;
        } else if (this.prod.EngravedPlateStatusID === 8) {
            this.ps.ep_id = 33146;
            this.ps.ep_id_view = 33146;
        } else if (this.prod.EngravedPlateStatusID === 9) {
            this.ps.ep_id_view = 33146;
        }

        const _uv_admin = getParameterByName('uv_admin');
        this.ui.is_uv_admin = !!_uv_admin;

        // ?ep_admin mirrors ?uv_admin: enables the Plate Position admin card for
        // calibrating the old-style plate overlay (xpos/ypos in settingsJSON).
        this.ui.is_ep_admin = !!getParameterByName('ep_admin');

        // A product is a UV-print product when it has a UV template (FrameParts
        // EntityTypeID = 20) assigned, delivered via RedisCacher as prod.uv_templates.
        // Select the base template (fall back to the first), parse its settingsJSON
        // ({ "uv_lines": [...] }) into the editable per-line array, and remember its
        // EntityID (uv_template_id -> ps.ep_id at ATC so EPContent persists).
        // contentText is the legacy home of the same JSON — a Redis product entry
        // cached before the settingsJSON migration has no settingsJSON yet, so fall
        // back to it until every product is recached.
        this.prod.uv_lines = [];
        this.prod.uv_template_id = 0;
        this.prod.uv_options = defaultUVOptions();
        const uvTemplates = this.prod.uv_templates || [];
        if (uvTemplates.length > 0) {
            // UV admin mode authors against uv_templates[0] only; shoppers get the base template.
            const t = _uv_admin ? uvTemplates[0] : uvTemplates.find(x => x.isBase) || uvTemplates[0];
            try {
                const cfg = JSON.parse(t.settingsJSON || t.contentText || '{}');
                const lines = Array.isArray(cfg) ? cfg : cfg.uv_lines;
                if (Array.isArray(lines) && lines.length > 0) {
                    this.prod.uv_lines = lines;
                    this.prod.uv_template_id = t.id;
                    // The template options (required personalization, locked font / colour,
                    // the photo's physical size) sit next to uv_lines; absent = the optional
                    // UV-on-mat behaviour.
                    this.prod.uv_options = parseUVOptions(Array.isArray(cfg) ? null : cfg.options);
                }
            } catch (e) {
                logger.error('Failed to parse UV template settingsJSON:', e);
            }
        }
        const hasUvTemplate = this.prod.uv_lines.length > 0;
        this.ps.is_uv_print = hasUvTemplate || !!_uv_admin;
        // A required template (the INFORMS awards) is personalized from the start: its
        // fixed lines print right away and the typed ones follow; there is no opt-in.
        this.ps.uv_print_active = hasUvTemplate && this.prod.uv_options.required;

        // Old-style engraved-plate overlay defaults; the plate-template init
        // (ep_type 5) and the admin ep_json (ep_type 6) below override these.
        this.ps.ep_xpos = 0;
        this.ps.ep_ypos = 0;
        this.ps.ep_zoom = 1;
        this.ps.ep_show_oldstyle = false;

        // Second plate: none until a template with plate2 (ep_type 5) or the admin
        // ep_json (ep_type 6) supplies one. EP2Width 0 = no second plate anywhere.
        this.ps.EP2Content = '';
        this.ps.EP2Width = 0;
        this.ps.EP2Height = 0;
        this.ps.ep2_xpos = 0;
        this.ps.ep2_ypos = 0;
        this.ps.ep2_zoom = 1;

        // ?uv_admin shows only uv_templates[0]'s real line data - no placeholder
        // seeding; with no template assigned the admin panel starts empty and JSON
        // can be pasted + applied.

        this.ps.lino_id = this.prod.LinoID;
        this.ps.lino_base_name = this.prod.orig_pcs.Base_LinoName;
        this.ps.lino_dyf_color = 'gold';
        this.ps.lino_is_dyf = this.prod.ProductID < 0;
        this.ps.is_horizontal = this.prod.IsHorizontal;
        this.ps.ProductTypeID = this.prod.ProductTypeID;

        // Capture the product's own EP method before any ep_type URL override, so Template mode (ep_type=5) can fall back to it when the product has no plate templates.
        const productEPMethodID = s.prod.EPMethodID;
        let ep_type = getParameterByName('ep_type');
        if (ep_type) {
            s.prod.EPMethodID = parseInt(ep_type, 10);
            this.ps.EPMethodID = s.prod.EPMethodID;
        } else if (this.prod.plate_templates != null && this.prod.plate_templates.length > 0) {
            this.ps.EPMethodID = 5;
            this.prod.EPMethodID = 5;
            ep_type = '5';
        }

        if (this.prod.IsCAF) {
            s.prod.EPMethodID = EPMethod.Old;
            s.prod.EPBorderType = EPBorderType.Inset;
            this.prod.EPSizeType = EPSizeType.Default;
            this.ps.EPMethodID = s.prod.EPMethodID;
            this.ps.EPBorderType = s.prod.EPBorderType;
            this.ps.EPSizeType = EPSizeType.Default;
        } else if (s.prod.EPMethodID > 1) {
            this.ps.EPMethodID = s.prod.EPMethodID;
        }

        if (this.prod.IsCAF && ep_type) {
            s.prod.EPMethodID = parseInt(ep_type, 10);
            this.ps.EPMethodID = s.prod.EPMethodID;
            s.prod.EPBorderType = EPBorderType.Inset;
            this.prod.EPSizeType = EPSizeType.AutoSnap;
            this.ps.EPBorderType = s.prod.EPBorderType;
            this.ps.EPSizeType = EPSizeType.AutoSnap;
            this.prod.EPWidth = 3;
            this.prod.EPHeight = 2;
            this.ps.EPWidth = 3;
            this.ps.EPHeight = 2;
        }

        if (ep_type === '6') {
            s.prod.EPMethodID = EPMethod.Admin;
            this.ps.EPMethodID = EPMethod.Admin;
            this.prod.EPSizeType = EPSizeType.Default;
            this.ps.EPSizeType = EPSizeType.Default;

            // Admin mode accepts an optional URL-encoded JSON blob (ep_json) carrying the
            // saved plate template properties: width, height (null when unset), and the
            // per-line titles (title1..title8) / placeholders (ph1..ph8). The backend builds
            // it in FramePart_Left_Properties.ascx.vb. getParameterByName already URL-decodes.
            const ep_json = getParameterByName('ep_json');
            if (ep_json) {
                try {
                    const epData = JSON.parse(ep_json);

                    // Size the plate to the saved template dimensions so the designer preview
                    // matches production. A null/<=0 dimension falls back to the default below.
                    const epWidth = Number(epData.ep_width);
                    const epHeight = Number(epData.ep_height);
                    if (Number.isFinite(epWidth) && epWidth > 0) {
                        this.prod.EPWidth = epWidth;
                        this.ps.EPWidth = epWidth;
                    }
                    if (Number.isFinite(epHeight) && epHeight > 0) {
                        this.prod.EPHeight = epHeight;
                        this.ps.EPHeight = epHeight;
                    }

                    // Store the per-line titles into the ep{i}_title fields, mirroring Template
                    // mode (ep_type === '5'). Placeholders (ph1..ph8) are parsed but not yet
                    // surfaced in the admin Quill editor UI.
                    for (let i = 1; i <= 10; i++) {
                        s.prod[`ep${i}_title`] = '';
                        this.ps[`ep${i}_title`] = '';
                    }
                    for (let i = 1; i <= 8; i++) {
                        const title = epData[`title${i}`];
                        if (typeof title === 'string' && title.length > 0) {
                            s.prod[`ep${i}_title`] = title;
                            this.ps[`ep${i}_title`] = title;
                        }
                    }

                    // Saved old-style overlay settings (backend forwards them from the
                    // template's settingsJSON) so the admin adjuster starts from them.
                    if (typeof epData.xpos === 'number') this.ps.ep_xpos = epData.xpos;
                    if (typeof epData.ypos === 'number') this.ps.ep_ypos = epData.ypos;
                    if (typeof epData.zoom === 'number' && epData.zoom > 0) this.ps.ep_zoom = epData.zoom;
                    if (epData.show_oldstyle !== undefined) this.ps.ep_show_oldstyle = !!epData.show_oldstyle;

                    // Second plate (settingsJSON.plate2, forwarded by the backend in ep_json): the
                    // designer preview paints both overlays and the position card can calibrate
                    // plate 2 too. The body keeps its raw [lineN] tokens here, like ep_content.
                    const plate2 = parseEPPlate2(epData.plate2);
                    this.ps.EP2Content = plate2 ? plate2.content : '';
                    this.ps.EP2Width = plate2 ? plate2.width : 0;
                    this.ps.EP2Height = plate2 ? plate2.height : 0;
                    this.ps.ep2_xpos = plate2 ? plate2.xpos : 0;
                    this.ps.ep2_ypos = plate2 ? plate2.ypos : 0;
                    this.ps.ep2_zoom = plate2 ? plate2.zoom : 1;
                } catch {
                    // Ignore malformed ep_json and fall back to the default size below.
                }
            }

            // Fill in any dimension ep_json did not provide. Per-dimension (not combined) so a
            // width-only or height-only ep_json keeps the value it did supply.
            if (!this.ps.EPWidth) {
                this.prod.EPWidth = EP_DEFAULT_FIXED_SIZE.width;
                this.ps.EPWidth = EP_DEFAULT_FIXED_SIZE.width;
            }
            if (!this.ps.EPHeight) {
                this.prod.EPHeight = EP_DEFAULT_FIXED_SIZE.height;
                this.ps.EPHeight = EP_DEFAULT_FIXED_SIZE.height;
            }

            // Admin mode accepts optional URL-encoded plate content to pre-fill the
            // editor. getParameterByName already URL-decodes the value.
            const ep_content = getParameterByName('ep_content');
            if (ep_content) {
                this.ps.EPContent = ep_content;
                if (s.pcs) s.pcs.EPContent = ep_content;
            }

            // Auto-add the engraved plate to the frame so the admin immediately sees it on the
            // canvas (ep_id > 0 enables it in web-only-render; ep_id_view holds the chosen color).
            this.ps.ep_id = this.ps.ep_id_view || 37;
        }

        if (ep_type === '5') {
            // Build the template dropdown from the product's DB-driven plate templates
            // (s.prod.plate_templates) instead of the hardcoded set. Each template's
            // contentText is the plate body HTML with [line1], [line2]… tokens.
            const plateTemplateOptions = mapPlateTemplatesToOptions(s.prod.plate_templates);

            if (plateTemplateOptions.length > 0) {
                s.prod.EPMethodID = EPMethod.Template;
                this.ps.EPMethodID = EPMethod.Template;
                s.prod.EPBorderType = EPBorderType.Inset;
                this.ps.EPBorderType = EPBorderType.Inset;
                this.prod.EPSizeType = EPSizeType.Default;
                this.ps.EPSizeType = EPSizeType.Default;
                s.prod.EPTemplates = plateTemplateOptions;

                // Template-mode plates are always attached to the frame: auto-add on load so the
                // plate shows on the canvas immediately (ep_id > 0 enables it in web-only-render).
                // Also clear any default lino — the renderer hides the engraved plate while a lino
                // is visible (core.ts), which left the reserved plate space empty. Mirrors the
                // manual ep_add() so auto-add behaves like clicking "Add Plate to Frame".
                this.ps.ep_id = this.ps.ep_id_view || 37;
                this.ps.lino_base_name = '';
                this.ps.lino_id = -1;
                this.prod.linos = [];
                this.prod.LinoID = -1;

                if (isInstPlateTemplateSet(plateTemplateOptions)) {
                    // INST-PLATES D6: a product whose templates carry plate_type gets the
                    // personalized-plate module (inst-plate/). It opens with no template, so
                    // the frame and the preview show a blank 3" x 1" plate in the finish of
                    // the product's insignia; the shopper picks size, type and template. DF4's
                    // ProductOpening opens these products in the same state.
                    this.ps.ep_id = this.instPlateFinish();
                    this.ps.ep_id_view = this.ps.ep_id;
                    this.ps.EPTemplateID = -1;
                    s.prod.EPTemplate = '';
                    this.ps.EPTemplate = '';
                    this.ps.EPContent = '';
                    s.prod.EPWidth = INST_PLATE_DEFAULT_SIZE.width;
                    s.prod.EPHeight = INST_PLATE_DEFAULT_SIZE.height;
                    this.ps.EPWidth = INST_PLATE_DEFAULT_SIZE.width;
                    this.ps.EPHeight = INST_PLATE_DEFAULT_SIZE.height;

                    for (let i = 1; i <= 10; i++) {
                        s.prod[`ep${i}_title`] = '';
                        this.ps[`ep${i}_title`] = '';
                    }
                } else {
                    // The plate template flagged IsBase is the original/selected one; fall back
                    // to the first template when none is flagged.
                    const baseTemplate = s.prod.plate_templates.find(p => p.isBase) || s.prod.plate_templates[0];
                    const initialTemplate = plateTemplateOptions.find(t => t.id === baseTemplate.id) || plateTemplateOptions[0];

                    this.ps.EPTemplateID = initialTemplate.id;
                    s.prod.EPWidth = initialTemplate.width;
                    s.prod.EPHeight = initialTemplate.height;
                    this.ps.EPWidth = initialTemplate.width;
                    this.ps.EPHeight = initialTemplate.height;
                    s.prod.EPTemplate = initialTemplate.template;
                    this.ps.EPTemplate = initialTemplate.template;

                    // Old-style overlay settings from the template's settingsJSON.
                    this.ps.ep_xpos = initialTemplate.xpos || 0;
                    this.ps.ep_ypos = initialTemplate.ypos || 0;
                    this.ps.ep_zoom = initialTemplate.zoom && initialTemplate.zoom > 0 ? initialTemplate.zoom : 1;
                    this.ps.ep_show_oldstyle = !!initialTemplate.showOldstyle;

                    // Second plate size/position from the template's settingsJSON.plate2 (the body
                    // is resolved from the lines by updateTemplateContent); zero when it has none.
                    this.ps.EP2Content = '';
                    this.ps.EP2Width = initialTemplate.plate2 ? initialTemplate.plate2.width : 0;
                    this.ps.EP2Height = initialTemplate.plate2 ? initialTemplate.plate2.height : 0;
                    this.ps.ep2_xpos = initialTemplate.plate2 ? initialTemplate.plate2.xpos : 0;
                    this.ps.ep2_ypos = initialTemplate.plate2 ? initialTemplate.plate2.ypos : 0;
                    this.ps.ep2_zoom = initialTemplate.plate2 ? initialTemplate.plate2.zoom : 1;

                    for (let i = 1; i <= 10; i++) {
                        s.prod[`ep${i}_title`] = '';
                        this.ps[`ep${i}_title`] = '';
                    }

                    for (const lt of initialTemplate.lineTitles) {
                        s.prod[`ep${lt.number}_title`] = lt.title;
                        this.ps[`ep${lt.number}_title`] = lt.title;
                    }
                }
            } else {
                // No plate templates configured: fall back to the product's own EP method
                // rather than forcing an empty Template mode.
                s.prod.EPMethodID = productEPMethodID;
                this.ps.EPMethodID = productEPMethodID;
            }
        }

        const ep_border = getParameterByName('ep_border');
        const isTemplateEP = ep_type === '5';

        if (ep_border && !isTemplateEP) {
            s.prod.EPBorderType = parseInt(ep_border, 10);
            this.ps.EPBorderType = s.prod.EPBorderType;
        }

        const ep_size = getParameterByName('ep_size');

        if (ep_size && !isTemplateEP) {
            this.prod.EPSizeType = parseInt(ep_size, 10);
            this.ps.EPSizeType = this.prod.EPSizeType;

            // if(!this.is_production){
            //     this.ps.ep_id = 1;
            //     this.ps.ep_id_view = 37;
            //     this.prod.EngravedPlateStatusID = 1;
            // }
        }

        if (this.prod.DYF_FrameStyleID === 4) {
            this.ps.vlbg_id = this.prod.VLBG_ID;
            const vlbg = s.prod.caf_mat2.filter(x => x.id === this.ps.vlbg_id)[0];
            if (vlbg) {
                this.ps.vlbg_color = vlbg.mat_color;
                this.ui.title_vlbg = vlbg.title;
            }
        }

        if (this.ps.is_caf) {
            this.ui.update_pricing = true;
            var _m = this.prod.mouldings.filter(x => x.isBase);
            if (_m.length > 0) {
                this.ps.moulding_id = _m[0].id;
                this.ui.moulding_th5 = !!_m[0].th5;
                this.ui.moulding_th6 = !!_m[0].th6;
                this.ui.moulding_zoom_path = _m[0].IMG_Zoom;
                this.ui.moulding_zoom_path_th = _m[0].IMG_Zoom_Thumb;
                this.ui.name_moulding = _m[0].name.toLowerCase();
                this.ui.title_moulding = _m[0].title;
            }
            var _i = this.prod.insignias.filter(x => x.isBase);
            if (_i.length > 0) {
                this.ps.insignia_id = _i[0].id;
            }
            var _m1 = this.prod.caf_mat1.filter(x => x.isBase);
            if (_m1.length > 0) {
                this.ps.mat1_id = _m1[0].id;
                this.ps.mat1_color = _m1[0].mat_color;
                this.ps.ins_gold_allowed = _m1[0].ins_gold_allowed;
                this.ps.ins_silver_allowed = _m1[0].ins_silver_allowed;
                this.ps.ins_black_allowed = _m1[0].ins_black_allowed;
                this.ui.linen = s.ui.linen = _m1[0].name.toLowerCase().includes('linen');
            }
            var _m2 = this.prod.caf_mat2.filter(x => x.isBase);
            if (_m2.length > 0) {
                this.ps.mat2_id = _m2[0].id;
                this.ps.mat2_color = _m2[0].mat_color;
            }
            s.ui.product_title = `Custom ${this.prod.DYF_FrameStyleName} Frame`;

            if (s.si.StoreID !== 136072) {
                s.ui.product_title = `Custom ${this.si.ProdInstText} ${this.prod.DYF_FrameStyleName} Frame`;
            }
            s.ui.product_title = s.ui.product_title.replace('Frame Frame', 'Frame');

            s.ui.moulding_has_zoom = true;
            s.ps.DocSizeTierID = 4;
            if (this.prod.DYF_FrameStyleID === 10) {
                this.selected_doc_size = 0;
            }

            // s.prod.allowUpload1 = Portal?.PortalID === 1 && this.prod.DYF_FrameStyleID === 1;
        } else if (this.prod.mats) {
            const mc = this.prod.mats.filter(x => x.id === this.prod.MatComboID)[0];
            if (mc) {
                this.process_color_restrictions_caf();
                this.ps.mat1_color = mc.pmat_color;
                this.ps.ins_gold_allowed = mc.ins_gold_allowed;
                this.ps.ins_silver_allowed = mc.ins_silver_allowed;
                this.ps.ins_black_allowed = mc.ins_black_allowed;
                this.ui.linen = s.ui.linen = mc.pmat_name.toLowerCase().includes('linen');
            }
        }

        this.process_color_restrictions_prod();

        if (this.prod.CustomizationMethod === 1) {
            this.ui.old_style_composite = (this.prod.OldCompositeImage || '').toLowerCase();
        }

        // hide year of graduation if linked store, or if diploma not in the product name
        if ((this.si.StoreTypeID === 5 && this.isRetail) || !this.ui.product_title.toLowerCase().includes('diploma') || this.ps.is_caf) {
            this.ui.show_grad_year = false;
        }
        if (this.prod.YearOfGraduation_Required) {
            this.ui.show_grad_year = true;
        }

        this.ui.allow_customer_photos = this.si.allow_customer_photos;
        this.ui.allow_reviews = this.si.allow_reviews;
        if (this.si.StoreID === 205255) {
            this.ui.allow_reviews = false;
        }
        this.ui.allow_smartbox = this.si.allow_smartbox;

        if (Portal?.DisableRealImages) {
            this.ui.allow_customer_photos = false;
        }

        if (Portal?.DisableReviews) {
            this.ui.allow_reviews = false;
        }

        DocSizes.Initialize();

        // share
        const sharedState = decodeShareStateFromUrl<ProductStateShare>(window.location.href);
        if (sharedState.ok) {
            try {
                let f: ProductStateShare = sharedState.value;
                // console.log(f);
                parseInt(f.m0.toString(), 10);
                parseInt(f.m1.toString(), 10);
                parseInt(f.m2.toString(), 10);
                parseInt(f.i.toString(), 10);
                parseInt(f.g.toString(), 10);
                parseInt(f.l.toString(), 10);

                s.ps.moulding_id = f.m0;
                s.ps.mat1_id = f.m1;
                s.ps.mat2_id = f.m2;

                if (f.i > 0) {
                    const m = s.prod.insignias.filter(m => m.id === f.i)[0];
                    if (m) {
                        s.ps.insignia_id = m.id;
                        s.ps.insignia_base_name = m.name;
                        s.ps.insignia_is_dyf = m.isCAF;
                        s.ps.insignia_dyf_color = m.ins_color.toLowerCase();
                        s.ui.name_insignia = m.name.toLowerCase();
                        s.ui.title_insignia = m.title;
                    }
                }

                if (f.l > 0) {
                    const m = s.prod.linos.filter(m => m.id === f.l)[0];
                    if (m) {
                        s.ps.lino_base_name = m.name;
                        s.ps.lino_is_dyf = false;
                        s.ps.lino_id = m.id;
                        s.ps.lino_text = m.title;
                        s.ps.lino_dyf_color = f.lc;
                        s.ps.ep_id = 0;
                    }
                }

                s.ps.glass_id = f.g;
                s.ps.lino_id = f.l;
                s.ps.ep_id = f.e;
                s.ps.ep1 = f.e1;
                s.ps.ep2 = f.e2;
                s.ps.ep3 = f.e3;
                s.ps.ep4 = f.e4;
                s.ps.ep5 = f.e5;
                s.ps.ep6 = f.e6;
                s.ps.ep7 = f.e7;
                s.ps.ep8 = f.e8;
                s.ps.ep9 = f.e9;
                s.ps.ep10 = f.e10;
                applySharedDocumentSizing(s.prod, s.ps, f);

                // Only adopt the plate attributes the payload actually carries. Extended mode never
                // assigns s.ps.EPWidth/EPHeight/EPBorderType/EPMethodID, so those keys are absent
                // from the share JSON, and the old fallbacks overwrote the product's own values
                // with -1/undefined. DefaultPCS.DefaultEPWidth is the worst of them: every layout
                // in core.ts seeds its EPWidth from it, so -1 sized the plate at -1 * DPI and it
                // vanished from the frame while the editor still showed the restored text.
                // prerender_set_pcs guards the same DefaultPCS writes with `> 0` — match it here.
                if (f.epm > 0) {
                    s.ps.EPMethodID = f.epm;
                    s.prod.EPMethodID = s.ps.EPMethodID;
                    if (s.pcs) s.pcs.EPMethodID = s.ps.EPMethodID;
                }

                if (typeof f.epb === 'number' && f.epb >= 0) {
                    s.ps.EPBorderType = f.epb;
                    s.prod.EPBorderType = s.ps.EPBorderType;
                    if (s.pcs) s.pcs.EPBorderType = s.ps.EPBorderType;
                }

                if (f.eps !== undefined && f.eps >= 0) {
                    s.ps.EPSizeType = f.eps;
                    s.prod.EPSizeType = f.eps;
                    if (s.pcs) s.pcs.EPSizeType = f.eps;
                } else if (f.epw > 0 && f.eph > 0) {
                    const _snapMatch = EP_SNAP_SIZES.findIndex(snap => snap.width === f.epw && snap.height === f.eph);
                    if (_snapMatch >= 0) {
                        s.ps.EPSizeType = EPSizeType.AutoSnap;
                        s.prod.EPSizeType = EPSizeType.AutoSnap;
                        if (s.pcs) s.pcs.EPSizeType = EPSizeType.AutoSnap;
                    }
                }

                if (f.epw > 0) {
                    s.ps.EPWidth = f.epw;
                    s.prod.EPWidth = s.ps.EPWidth;
                    if (s.pcs) s.pcs.EPWidth = s.ps.EPWidth;
                    DefaultPCS.DefaultEPWidth = s.ps.EPWidth;
                }

                if (f.eph > 0) {
                    s.ps.EPHeight = f.eph;
                    s.prod.EPHeight = s.ps.EPHeight;
                    if (s.pcs) s.pcs.EPHeight = s.ps.EPHeight;
                    DefaultPCS.DefaultEPHeight = s.ps.EPHeight;
                }

                if (f.epc) {
                    s.ps.EPContent = decodeURIComponent(f.epc.replace(/\+/g, ' '));
                    if (s.pcs) s.pcs.EPContent = s.ps.EPContent;
                }

                // A personalized plate (INST-PLATES) comes back with its template (t), size
                // and type, and the saved lines and HTML; the shopper re-approves it.
                if (this.isInstPlate) {
                    this.restoreInstPlateShare(f);
                }

                // UV print personalization (separate from the engraved plate): uvc is the
                // URL-encoded uv_lines JSON saved at add-to-cart, so the cart's Edit Frame
                // link restores the shopper's text / font / color. Values merge by line
                // index into the template lines already loaded from Redis above; skip when
                // the product's UV template has changed since the line was carted.
                if (f.uvc && this.prod.uv_lines.length > 0 && !(f.ptid > 0 && this.prod.uv_template_id > 0 && f.ptid !== this.prod.uv_template_id)) {
                    const uvCfg = JSON.parse(decodeURIComponent(f.uvc.replace(/\+/g, ' ')));
                    const uvSaved = Array.isArray(uvCfg) ? uvCfg : uvCfg.uv_lines;
                    if (Array.isArray(uvSaved)) {
                        for (const savedLine of uvSaved) {
                            if (!savedLine || typeof savedLine.value !== 'string' || savedLine.value.trim() === '') continue;
                            const line = this.prod.uv_lines.find(x => x.i === savedLine.i);
                            if (!line) continue;
                            line.value = savedLine.value;
                            if (savedLine.font) line.font = savedLine.font;
                            if (savedLine.color) line.color = savedLine.color;
                            this.ps.uv_print_active = true;
                        }
                    }
                }

                // console.log(f);
            } catch (err: any) {
                logger.error(err);
            }
        }

        this.ui.doRender = true;
        this.ui.update_pricing = true;

        let clone;
        try {
            clone = structuredClone(this.prod.orig_pcs);
        } catch (e) {
            clone = JSON.parse(JSON.stringify(this.prod.orig_pcs));
        }
        this.pcs = clone;
        this.pcs.ProductTypeID = this.prod.ProductTypeID;

        this.pulse();
        this.ready = true;

        // marketing
        if (this.isRetail && !this.prod.IsCAF) {
            const marketing_item: ga4_item[] = [
                {
                    item_id: s.prod.IsCAF ? `CAF-${s.prod.ProductID}` : s.ps.item_id,
                    item_name: s.prod.Name,
                    coupon: '',
                    currency: 'USD',
                    discount: 0,
                    index: 1,
                    item_brand: __company_name,
                    item_category: s.prod.ProductTypeDisplayName,
                    item_category2: s.si.InstName,
                    location_id: 'ChIJUcHLaPbi54kRtbVMrbSH4MU',
                    price: this.ui.price,
                    quantity: 1,
                    product_id: s.prod.ProductID,
                    item_list_name: s.prod.IsCAF ? `${this.si.InstName}` : this.si.StoreName,
                    item_list_id: this.si.StoreID.toString(),
                    item_variant: this.prod.ItemID,
                },
            ];

            // marketing.js is a separate script that ad blockers and failed loads leave out; the page must render without it
            if (typeof marketing_product_visit === 'function') {
                marketing_product_visit(marketing_item);
            }
        }

        // window.addEventListener('resize', util.debounce(this.pulse(true), 150));
        if (!this.resizeListenerAttached) {
            window.addEventListener('resize', this.onWindowResize);
            this.resizeListenerAttached = true;
        }

        try {
            let item_image = {
                ProductName: s.prod.Name,
                ProductID: s.prod.ProductID,
                SKU: s.prod.ItemID,
                URL: `https://${window.location.host}${s.si.URL}`,
                Brand: __company_name,
                Price: s.si.Price_Retail,
                ImageUrl: s.prod.CustomizationMethod === 2 ? `https://${Portal?.CDNDomain}/images/products/gen/products/${s.prod.ItemID.toLowerCase()}_${s.prod.ProductID}_1280_1280.png` : `https://${Portal?.CDNDomain}/images/products/full/${s.prod.Image_Full.toLowerCase()}`,
                PortalID: Portal?.PortalID,
            };

            let item_noimage = {
                ProductName: s.prod.Name,
                ProductID: s.prod.ProductID,
                SKU: s.prod.ItemID,
                URL: `https://${window.location.host}${s.si.URL}`,
                Brand: __company_name,
                Price: s.si.Price_Retail,
                PortalID: Portal?.PortalID,
            };

            if (!s.prod.IsCAF && s.prod.CustomizationMethod === 2) {
                klaviyo.push(['track', 'Viewed Product', item_image]);
            } else if (s.prod.IsCAF) {
                klaviyo.push(['track', 'Viewed Product', item_noimage]);
            } else if (s.prod.Image_Full && s.prod.Image_Full.length > 5) {
                klaviyo.push(['track', 'Viewed Product', item_image]);
            } else {
                klaviyo.push(['track', 'Viewed Product', item_noimage]);
            }
            klaviyo.push(['identify', {$lastPortal: Portal?.PortalID}]);
        } catch (errk) {
            logger.error(errk);
        }
    };

    @action
    give_ep_color_id = (id: number): number => {
        if (!this.ps.ins_silver_allowed && id === 39) {
            if (this.ps.ins_gold_allowed) {
                return 37;
            } else {
                return 39;
            }
        } else if (!this.ps.ins_gold_allowed && (id === 37 || id < 1)) {
            if (this.ps.ins_silver_allowed) {
                return 39;
            } else {
                return 37;
            }
        } else if (!this.ps.ins_black_allowed && id === 1099) {
            if (this.ps.ins_gold_allowed) {
                return 37;
            } else {
                return 39;
            }
        }
        return id;
    };

    @action
    pulse_fast_ship = (): void => {
        if (!this.__fast_ship_orig || this.prod.IsCAF || !this.isRetail) {
            __fast_ship = false;
            this.ui.fast_ship_now = false;
            return;
        }

        var diffFromFastShip = false;

        if (s.prod.IsFrame && s.ps.glass_id > 1) {
            diffFromFastShip = true;
        }
        if (s.prod.IsFrame && s.ps.moulding_id !== s.prod.MouldingID) {
            diffFromFastShip = true;
        }
        if (s.prod.IsFrame && s.ps.mat1_id !== s.prod.Mat1_ID) {
            diffFromFastShip = true;
        }
        if (s.prod.IsFrame && s.ps.mat2_id !== s.prod.Mat2_ID) {
            diffFromFastShip = true;
        }
        if (s.prod.IsFrame && s.ps.insignia_id !== s.prod.InsigniaID) {
            diffFromFastShip = true;
        }
        if (s.prod.IsFrame && s.ps.lino_id !== s.prod.LinoID) {
            diffFromFastShip = true;
        }
        if (s.prod.IsFrame && s.si.AutomatedDocSizes && s.docsizes_enabled && s.docsizes_available && s.docsizes_selected_id !== s.docsizes_orig_id) {
            diffFromFastShip = true;
        }

        if (__fast_ship && diffFromFastShip && !this.__fast_ship_warned && !this.fast_ship_notice_inline) {
            // warn
            this.__fast_ship_warned = true;
            modal('modal-fast-ship-warning');
        }
        __fast_ship = !diffFromFastShip;
        this.ui.fast_ship_now = !diffFromFastShip;
    };

    @action
    pulse = (forceRender?: boolean) => {
        restoreFixedDisplaySizing(this.prod, this.ps);
        // if(!this.ui.msrp || this.ui.msrp < 0){this.ui.msrp = 0;}
        this.pulse_fast_ship();
        if (!this.ps.is_caf) {
            this.pulse_product();
        } else {
            this.pulse_caf();
        }

        // todo i, if invalid insignia or EP elected, switch it to allowed versiin
        // s.ps.ep_id_view === 1099 ? 'black' : s.ps.ep_id_view === 39
        // color enforcement:)
        if (this.prod.IsCAF) {
            this.process_color_restrictions_caf();

            if (this.ps.ep_id > 0) {
                this.ps.ep_id = this.give_ep_color_id(this.ps.ep_id);
            }

            this.ps.ep_id_view = this.give_ep_color_id(this.ps.ep_id_view);
            this.enforce_color_selections_caf();
        }

        this.process_color_restrictions_prod();

        // EP color fallback after all color restrictions are applied
        if (s.prod.CustomizationMethod > 1 && (this.ps.ep_id > 0 || this.ps.ep_id_view > 0)) {
            const epFixed = this.give_ep_color_id(this.ps.ep_id > 0 ? this.ps.ep_id : this.ps.ep_id_view);
            if (this.ps.ep_id > 0 && this.ps.ep_id !== epFixed) {
                this.ps.ep_id = epFixed;
                this.ui.doRender = true;
            }
            if (this.ps.ep_id_view > 0 && this.ps.ep_id_view !== epFixed) {
                this.ps.ep_id_view = epFixed;
            }
        }

        // INST-PLATES D6: a personalized plate is always on the frame and its finish follows
        // the selected insignia (silver 39, else gold 37), whatever the status or the colour
        // fallback above chose. DF4's ProductState applies the same rule to the cart line.
        if (this.isInstPlate) {
            const finish = this.instPlateFinish();
            if (this.ps.ep_id !== finish) {
                this.ps.ep_id = finish;
                this.ui.doRender = true;
            }
            this.ps.ep_id_view = finish;
        }

        if (this.ui.doRender || forceRender) {
            prerender_set_pcs();
            this.ps.css = r.render(this.pcs, RenderType.Normal);

            this.glass_update();
            this.setUI();

            if (this.RenderingV2) {
                this.frameV2 = r.renderV2(this.pcs, RenderType.Normal);
            }
        }
        if (this.ui.update_pricing) {
            this.ui.update_pricing = false;
            pricing();
        }
        if (this.ui.update_doc_sizes) {
            updateShippingBoxID();
        }

        this.ui.show_atc = true;
        this.ui.is_bo = false;
        this.ui.bo_msg = '';
        if (this.si.StoreTypeID === 5 && this.isRetail) {
            this.ui.show_atc = false;
            this.ui.show_pricing = false;
        } else if (this.isRetail && this.prod && this.prod.BO_Single) {
            this.ui.show_atc = false;
            this.ui.is_bo = true;
            this.ui.bo_msg = s.prod.BO_OverrideText;
        } else if ((this.isRetail || this.prod.IsBO_MyFrames) && this.prod.BO_Text !== '' && this.prod.BO_FramePartID === this.ps.moulding_id) {
            this.ui.show_atc = false;
            this.ui.is_bo = true;
            this.ui.bo_msg = s.prod.BO_Text;
        } else if (!this.isRetail && this.prod.BO_MyFramesText !== '') {
            this.ui.show_atc = false;
            this.ui.is_bo = true;
            this.ui.bo_msg = this.prod.BO_MyFramesText;
        }
    };

    // A product whose plate templates carry a plate_type (INST-PLATES): the personalized-plate
    // module replaces EngravedPlateExtended, and Add to Cart reads "Approve & Add to Cart" and
    // runs its gate. s.prod.EPTemplates exists only in template mode (init, ep_type 5).
    get isInstPlate(): boolean {
        return isInstPlateTemplateSet(this.prod?.EPTemplates);
    }

    // The personalized plate's finish for the selected insignia (inst-plate-rules plateFinishFor).
    instPlateFinish = (): number => plateFinishFor((this.prod?.insignias || []).find(m => m.id === this.ps.insignia_id)?.ins_color);

    // Share-link restore of a personalized plate: the saved template when the product still
    // links it (size and type follow it; ep1..ep10 and EPContent stay as the link saved them),
    // else no template, a blank plate at the link's size and the lines kept. It is always a
    // template plate at its default border and size type, whatever epm/epb/eps the link has.
    // The certification and the proof confirmation start cleared (inst-plate.store defaults).
    @action
    restoreInstPlateShare = (f: ProductStateShare) => {
        const restored = restoreFromShare(f, this.prod.EPTemplates);

        this.prod.EPMethodID = EPMethod.Template;
        this.ps.EPMethodID = EPMethod.Template;
        this.prod.EPBorderType = EPBorderType.Inset;
        this.ps.EPBorderType = EPBorderType.Inset;
        this.prod.EPSizeType = EPSizeType.Default;
        this.ps.EPSizeType = EPSizeType.Default;

        if (restored.template) {
            restoreTemplate(restored.template);
        } else {
            clearTemplate();
            setTemplatePlateSize(restored.size.width, restored.size.height);
        }
    };

    // Template-mode plates require every visible (titled) line to be filled before checkout,
    // and any line with a validation pattern (settings-driven numbers/text/regex, or a legacy
    // rgx() placeholder) to match it. Returns the titles of any visible line the customer left
    // blank or filled invalidly — [] when complete, not in Template mode, or the plate was
    // removed (ep_id <= 0). Mirrors EngravedPlateExtended's visibleTemplateLines (title from
    // prod/ps ep{i}_title, value from ps.ep{i}, spec/placeholder from the active EPTemplate).
    // Read by add_to_cart to block an incomplete template plate.
    get epTemplateMissingLineTitles(): string[] {
        if (this.ps.EPMethodID !== EPMethod.Template || this.ps.ep_id <= 0) {
            return [];
        }

        const tpl = (this.prod.EPTemplates || []).find(t => t.id === this.ps.EPTemplateID);
        const missing: string[] = [];
        for (let i = 1; i <= 10; i++) {
            const title = (this.prod[`ep${i}_title`] || this.ps[`ep${i}_title`] || '').trim();
            if (title === '') continue;
            const value = this.ps[`ep${i}`] || '';
            const lineDef = tpl?.lineTitles.find(lt => lt.number === i);
            // An optional line (ep_lines[].optional) may stay blank; a filled one still has
            // to pass its pattern.
            if (isNullOrWhiteSpace(value) && lineDef?.optional) continue;
            if (isNullOrWhiteSpace(value) || epLineFailsRegex(epLineSpec(lineDef), value)) {
                missing.push(title);
            }
        }
        return missing;
    }

    @action
    getPPIValuesForImageUpload = () => {
        const _uploadCheck_W = !this.prod.docWidth || this.prod.docWidth < 1 ? this.ps.docWidth : this.prod.docWidth;
        const _uploadCheck_H = !this.prod.docHeight || this.prod.docHeight < 1 ? this.ps.docHeight : this.prod.docHeight;

        if (this.prod && _uploadCheck_W > 0) {
            this.ppi_w = _uploadCheck_W * 150;
            this.ppi_h = _uploadCheck_H * 150;
            this.ppi_w_max = _uploadCheck_W * 300;
            this.ppi_h_max = _uploadCheck_H * 300;
        }
    };

    @action
    pulse_product = () => {
        this.ui.sku = this.ui.item_id === this.ps.base_item_id ? this.si.SKU : '';
        this.ui.item_id = `${this.ps.customized_core ? 'C' : ''}${this.ps.customized_glass ? 'G' : ''}${this.ps.base_item_id}`;
        this.ps.DocSizeTierID = r.Get_DocumentSizeTierID(this.ps.docWidth, this.ps.docHeight, this.prod.ProductTypeID);

        // if(this.ui.mat_change){
        //     this.ui.mat_change = false;
        //     const m = s.prod.mats.filter(m => m.id === this.ps.matcombo_id)[0];
        //     this.ps.css.pmat_BackgroundImage = this.ps.css.pmat_BackgroundImage.replace(/(v2\/)(.*)(_)/g, `v2/${m.pmat_name.toLowerCase()}_`);

        //     const isFillet = (m.mat2_id === 36 || m.mat1_id === 38);
        //     this.ps.css.fillet_visible = isFillet;
        //     const smat = `#${m.smat_color}`;
        //     this.ps.css.doc1_smat_backgroundColor = smat;
        //     this.ps.css.doc2_smat_backgroundColor = smat;
        //     this.ps.css.tassel_smat_backgroundColor = smat;
        //     this.ps.css.varsity_smat_backgroundColor = smat;
        //     this.ps.css.oval_smat_backgroundColor = smat;
        //     this.ps.css.spirit_smat_bg = smat;

        //     if(isFillet){

        //     } else {

        //     }
        // }

        // this.ui.price = this.isRetail ? this.si.price_Retail : this.si.price_Wholesale;
    };

    @action
    pulse_caf = () => {
        this.ps.DocSizeTierID = r.Get_DocumentSizeTierID(this.ps.docWidth, this.ps.docHeight, this.prod.ProductTypeID);
    };

    @action
    glass_update = () => {
        for (let i = 0; i < this.glass.length; i++) {
            this.glass[i].price = currency_noNegatives(this.getGlassPrice(this.prod.orig_pcs, s.ps.DocSizeTierID, this.glass[i].id)).replace('.00', '');
        }
    };

    @action
    process_color_restrictions_caf = () => {
        if (!this.prod.IsCAF) {
            return;
        }
        const mat = s.prod.caf_mat1.filter(x => x.id === s.ps.mat1_id)[0];
        const m = s.prod.mouldings.filter(x => x.id === s.ps.moulding_id)[0];
        // console.log(m.dyf_color);
        if (mat && m) {
            this.ps.ins_gold_allowed = mat.ins_gold_allowed && m.dyf_color !== 'no gold';
            this.ps.ins_silver_allowed = mat.ins_silver_allowed && m.dyf_color !== 'no silver';
            this.ps.ins_black_allowed = mat.ins_black_allowed && m.dyf_color !== 'no black';
        }
    };

    @action
    enforce_color_selections_caf = () => {
        const fallbacks: Record<string, string[]> = {
            gold: ['silver', 'black'],
            silver: ['black', 'gold'],
            black: ['gold', 'silver'],
        };

        const isAllowed = (c: string) => (c === 'gold' ? this.ps.ins_gold_allowed : c === 'silver' ? this.ps.ins_silver_allowed : c === 'black' ? this.ps.ins_black_allowed : true);

        const getFallback = (current: string): string | null => {
            if (isAllowed(current)) return null;

            let result: string | null = null;

            for (const c of fallbacks[current] ?? []) {
                if (isAllowed(c)) result = c;
            }

            return result;
        };

        const linoFallback = getFallback(s.ps.lino_dyf_color);

        if (linoFallback) {
            s.ps.lino_dyf_color = linoFallback;
        }

        if (this.ps.insignia_id > 0) {
            const oldColor = s.ps.insignia_dyf_color;
            const insigniaFallback = getFallback(oldColor);

            if (insigniaFallback) {
                s.ps.insignia_dyf_color = insigniaFallback;

                const newName = this.ps.insignia_base_name.toLowerCase().replace(oldColor, insigniaFallback);
                const new_ins = this.prod.insignias.filter(x => x.name.toLowerCase() === newName);

                if (new_ins && new_ins.length > 0) {
                    this.ps.insignia_id = new_ins[0].id;
                    this.ps.insignia_base_name = new_ins[0].name;
                    this.ps.insignia_is_dyf = new_ins[0].isCAF;
                    this.ui.name_insignia = new_ins[0].name.toLowerCase();
                    this.ui.title_insignia = new_ins[0].title;
                } else {
                    this.ps.insignia_id = -1;
                    this.ui.title_insignia = '';
                }

                this.ui.doRender = true;
            }
        }

        if (this.ps.insignia_id > 0 && s.ui.linen && s.ps.insignia_base_name.toLowerCase().includes('embossed')) {
            this.ps.insignia_id = -1;
            this.ui.title_insignia = '';
            this.ui.doRender = true;
        }

        // If gold EP is selected but gold is no longer allowed, fall back to silver
        if (s.prod.CustomizationMethod > 1) {
            if (this.ps.ep_id === 37 && !this.ps.ins_gold_allowed && this.ps.ins_silver_allowed) {
                this.ps.ep_id = 39;
                this.ps.ep_id_view = 39;
                this.ui.doRender = true;
            }
            // If silver EP is selected but silver is no longer allowed, fall back to gold
            if (this.ps.ep_id === 39 && !this.ps.ins_silver_allowed && this.ps.ins_gold_allowed) {
                this.ps.ep_id = 37;
                this.ps.ep_id_view = 37;
                this.ui.doRender = true;
            }

            // If gold inner accent is selected, switch silver EP to gold
            if (this.ps.mat2_id === 385) {
                if (this.ps.ep_id === 39) {
                    this.ps.ep_id = 37;
                    this.ui.doRender = true;
                }
                if (this.ps.ep_id_view === 39) {
                    this.ps.ep_id_view = 37;
                }
            }
        }
    };

    // A lino follows the primary-mat rule the seals follow: a mat combo whose primary mat has Insignia_Black_Enabled (or the gold
    // or silver flag) off does not offer a lino in that colour (Freshservice 9784: black grad linos on Black and Navy mats).
    // The product's own lino always stays. CAF frames pick lino colours through ins_*_allowed already.
    linoAllowedOnMat = (lino: LinoCustomization): boolean => {
        if (!this.prod || this.prod.IsCAF || lino.isBase || lino.id === this.prod.LinoID) return true;
        const mat = this.prod.mats?.find(i => i.id === this.ps.matcombo_id);
        if (!mat) return true;
        const color = (lino.desc4 || '').trim().toLowerCase();
        return color === 'black' ? mat.ins_black_allowed : color === 'gold' ? mat.ins_gold_allowed : color === 'silver' ? mat.ins_silver_allowed : true;
    };

    // A mat change can rule out the picked lino's colour: switch to the same lino in a colour the mat allows, else back to the
    // product's own lino (as restoreOriginalLino does).
    @action
    keep_lino_allowed_on_mat = () => {
        const picked = this.prod.linos?.find(i => i.id === this.ps.lino_id);
        if (!picked || this.linoAllowedOnMat(picked)) return;

        const base = getLinoOptionBase(picked);
        const twin = this.prod.linos.find(i => i.id !== picked.id && getLinoOptionBase(i) === base && this.linoAllowedOnMat(i));
        const next = twin ? this.prod.linos.find(i => i.id === this.preferredLinoId(twin.id)) ?? twin : undefined;
        if (next) {
            this.ps.lino_id = next.id;
            this.ps.lino_base_name = next.name;
            this.ps.lino_text = next.title;
        } else {
            this.ps.lino_id = this.preferredLinoId(this.prod.LinoID);
            this.ps.lino_base_name = this.prod.linos.find(i => i.id === this.ps.lino_id)?.name || this.prod.orig_pcs.Base_LinoName;
            this.ps.lino_text = this.ps.lino_base_name;
            this.ps.lino_dyf_color = 'Gold';
            this.ps.lino_is_dyf = false;
        }
        this.ui.update_pricing = true;
        this.ui.doRender = true;
    };

    @action
    process_color_restrictions_prod = () => {
        if (this.prod.IsCAF) {
            return;
        }

        const mat = s.prod.mats.find(i => i.id === s.ps.matcombo_id);

        if (!mat) {
            this.ps.ins_gold_allowed = true;
            this.ps.ins_silver_allowed = true;
            this.ps.ins_black_allowed = true;
            return;
        }

        this.ps.ins_gold_allowed = mat.ins_gold_allowed;
        this.ps.ins_silver_allowed = mat.ins_silver_allowed;
        this.ps.ins_black_allowed = mat.ins_black_allowed;

        this.keep_lino_allowed_on_mat();
        const lino = s.prod.linos.find(i => i.id === s.ps.lino_id);

        if (lino) {
            const ifLinoIsNot = (color: string) => lino.desc4 !== color && !lino.name.toLowerCase().includes(color);

            // Gold and silver are conflicting metallics, so a gold lino disallows a silver
            // insignia and vice-versa. Black has no conflicting counterpart — a black lino must
            // not disallow a black insignia (CHC: original black lino was hiding the black
            // insignia), so ins_black_allowed stays as derived from the mat above.
            this.ps.ins_silver_allowed = ifLinoIsNot('gold') && this.ps.ins_silver_allowed;
            this.ps.ins_gold_allowed = ifLinoIsNot('silver') && this.ps.ins_gold_allowed;
        }
    };

    @action
    setUI = () => {
        const moulding = s.prod.mouldings.filter(m => m.id === s.ps.moulding_id)[0];

        const _glass = s.glass.filter(m => m.id === this.ps.glass_id)[0];

        if (moulding) {
            this.ui.name_moulding = moulding.name.toLowerCase();
            this.ui.title_moulding = moulding.title;

            this.ui.moulding_has_zoom = moulding.has_zoom;
            this.ui.moulding_zoom_path = moulding.IMG_Zoom;
            this.ui.moulding_zoom_path_th = moulding.IMG_Zoom_Thumb;
        }
        if (this.ps.is_caf) {
            this.ui.moulding_has_zoom = true;
        }

        if (!this.ps.is_caf) {
            const mat = s.prod.mats.filter(m => m.id === s.ps.matcombo_id)[0];
            if (mat) {
                this.ui.name_mat1 = mat.pmat_name.toLowerCase();
                this.ui.name_mat2 = mat.smat_name.toLowerCase();

                this.ui.title_mat_combo = mat.title;
                this.ui.title_mat1 = mat.pmat_title;
                this.ui.title_mat2 = mat.smat_title;

                this.ui.color_mat1 = mat.pmat_color;
                this.ui.color_mat2 = mat.smat_color;
            }
        } else {
            const m1 = s.prod.caf_mat1.filter(m => m.id === s.ps.mat1_id)[0];
            const m2 = s.prod.caf_mat2.filter(m => m.id === s.ps.mat2_id)[0];
            if (m1) {
                this.ui.name_mat1 = m1.name.toLowerCase();
                this.ui.title_mat1 = m1.title;
                this.ui.color_mat1 = m1.mat_color;
            }
            if (m2) {
                this.ui.name_mat2 = m2.name.toLowerCase();
                this.ui.title_mat2 = m2.title;
                this.ui.color_mat2 = m2.mat_color;
            }
        }

        if (_glass) {
            this.ui.name_glass = _glass.title;
        }
    };

    blankFunction = () => {
        const x = 1;
    };

    closeTooltips = () => {
        setTimeout(function () {}, 1500);
    };

    @action
    share = async (): Promise<void> => {
        const ps: ProductStateShare = {
            m0: s.ps.moulding_id,
            m1: s.ps.mat1_id,
            m2: s.ps.mat2_id,
            i: s.ps.insignia_id,
            g: s.ps.glass_id,
            l: s.ps.lino_id,
            e: s.ps.ep_id,
            e1: s.ps.ep1,
            e2: s.ps.ep2,
            e3: s.ps.ep3,
            e4: s.ps.ep4,
            e5: s.ps.ep5,
            e6: s.ps.ep6,
            e7: s.ps.ep7,
            e8: s.ps.ep8,
            e9: s.ps.ep9,
            e10: s.ps.ep10,
            o: s.ps.is_horizontal,
            w1: s.ps.docWidth,
            w2: s.ps.docWidth2,
            h1: s.ps.docHeight,
            h2: s.ps.docHeight2,
            lc: s.ps.lino_dyf_color,
            epc: encodeURIComponent(s.ps.EPContent),
            epw: s.ps.EPWidth,
            eph: s.ps.EPHeight,
            epb: s.ps.EPBorderType,
            epm: s.ps.EPMethodID,
            eps: s.ps.EPSizeType,
            uvc: s.ps.is_uv_print && s.ps.uv_print_active && Array.isArray(s.prod.uv_lines) && s.prod.uv_lines.length > 0 ? encodeURIComponent(uvContentJSON(s.prod.uv_lines, s.prod.uv_options)) : '',
            ptid: s.prod.uv_template_id || 0,
            egs: '',
            t: s.ps.EPTemplateID > 0 ? s.ps.EPTemplateID : 0,
            por: Portal?.PortalID as number,
        };

        const shareData = {
            title: `${s.si.InstName} ${s.ui.product_title}`,
            text: `Shared with you: ${s.si.InstName} ${s.ui.product_title} via diplomaframe.com`,
            url: buildShareUrl(window.location.href, toJS(ps)),
        };

        try {
            window.dataLayer.push({
                event: 'share',
                event_params: {
                    item_id: s.ps.base_item_id,
                    method: 'product-page',
                    content_type: 'product',
                },
            });
        } catch (err) {
            logger.error(err);
        }

        // console.log(JSON.stringify(toJS(ps)).length);
        // console.log(encodeURIComponent(JSON.stringify(toJS(ps))).length);
        // console.log(shareData.url.length);
        try {
            await navigator.share(shareData);
        } catch (err) {
            logger.error(err);
        }
    };

    @action
    getGlassPrice = (pcs: ProductCustomizationState, DocSizeTierID: number, GlassID: number): number => {
        const photo_frames = [10, 50, 56, 57, 58, 59, 60];
        const isPhotoFrame = photo_frames.includes(pcs.ProductTypeID);
        let Glass_DocSizeTierID = -0;

        if (isPhotoFrame) {
            if (DocSizeTierID === 1) {
                Glass_DocSizeTierID = 1;
            } else if (DocSizeTierID === 14) {
                Glass_DocSizeTierID = 14;
            } else if (DocSizeTierID === 2 || DocSizeTierID === 13) {
                Glass_DocSizeTierID = 2;
            } else if (DocSizeTierID >= 3 && DocSizeTierID <= 5) {
                Glass_DocSizeTierID = 5;
            } else {
                Glass_DocSizeTierID = 8;
            }
        } else {
            if (DocSizeTierID < 3 || DocSizeTierID === 13) {
                Glass_DocSizeTierID = 2;
            } else if (DocSizeTierID === 14) {
                Glass_DocSizeTierID = 14;
            } else if (DocSizeTierID >= 3 && DocSizeTierID <= 5) {
                Glass_DocSizeTierID = 5;
            } else {
                Glass_DocSizeTierID = 8;
            }
        }

        const isOversized: boolean = __oversized.includes(pcs.ProductTypeID);
        // console.log('isOversized', isOversized);

        if (isOversized) {
            if (Glass_DocSizeTierID === 14) {
                Glass_DocSizeTierID = 2;
            } else if (Glass_DocSizeTierID === 2) {
                Glass_DocSizeTierID = 5;
            } else if (Glass_DocSizeTierID === 5) {
                Glass_DocSizeTierID = 8;
            }
        }

        // console.log('Glass_DocSizeTierID', Glass_DocSizeTierID);

        let price: number = 0;
        if (!s.isRetail) {
            price = s.glass_prices.filter(x => x.GlassID === GlassID && x.DocumentSizeTierID === Glass_DocSizeTierID)[0].Price_Wholesale;
        } else {
            price = s.glass_prices.filter(x => x.GlassID === GlassID && x.DocumentSizeTierID === Glass_DocSizeTierID)[0].Price;
        }

        if (isOversized && GlassID !== 1) {
            price += 10;
        }

        return price;
    };

    @action
    DebugSetLino() {
        // const startDate = new Date(2020, 1, 1);
        // const endDate = new Date(2030, 1, 1);
        // const startDate2 = new Date(2000, 1, 1);
        // const endDate2 = new Date(2019, 12, 31);
        // const startDate3 = new Date(1990, 1, 1);
        // const endDate3 = new Date(1999, 12, 31);
        // this.docsizes_enabled = true;
        // this.si.AutomatedDocSizes = true;
        // this.si.ProductDocSizes = [
        //     {Doc_Width: 12.5, Doc_Height: 10.5, DocSizeName: 'Bachelors', Id: 1, DocType: 1, StartDate: startDate3, EndDate: endDate3, LicensorKey: 206, StatusID: 1, GradSchoolID: -1, ApplyToGradSchools: false, earliest_start_date: false, latest_start_date: false, has_different_heights: false, has_different_widths: false, has_different_years: false},
        //     {Doc_Width: 8.5, Doc_Height: 11, DocSizeName: 'Bachelors', Id: 2, DocType: 1, StartDate: startDate, EndDate: endDate, LicensorKey: 206, StatusID: 1, GradSchoolID: -1, ApplyToGradSchools: false, earliest_start_date: false, latest_start_date: false, has_different_heights: false, has_different_widths: false, has_different_years: false},
        //     {Doc_Width: 11, Doc_Height: 14, DocSizeName: 'Associates', Id: 3, DocType: 1, StartDate: startDate, EndDate: endDate, LicensorKey: 206, StatusID: 1, GradSchoolID: -1, ApplyToGradSchools: false, earliest_start_date: false, latest_start_date: false, has_different_heights: false, has_different_widths: false, has_different_years: false},
        //     {Doc_Width: 14, Doc_Height: 17, DocSizeName: 'Bachelors', Id: 4, DocType: 1, StartDate: startDate2, EndDate: endDate2, LicensorKey: 206, StatusID: 1, GradSchoolID: -1, ApplyToGradSchools: false, earliest_start_date: false, latest_start_date: false, has_different_heights: false, has_different_widths: false, has_different_years: false},
        //     {Doc_Width: 8.5, Doc_Height: 11, DocSizeName: 'PhD', Id: 5, DocType: 1, StartDate: startDate2, EndDate: endDate2, LicensorKey: 206, StatusID: 1, GradSchoolID: 3946, ApplyToGradSchools: false, earliest_start_date: false, latest_start_date: false, has_different_heights: false, has_different_widths: false, has_different_years: false},
        //     {Doc_Width: 14, Doc_Height: 11, DocSizeName: 'PhD', Id: 6, DocType: 1, StartDate: startDate, EndDate: endDate, LicensorKey: 206, StatusID: 1, GradSchoolID: 3946, ApplyToGradSchools: false, earliest_start_date: false, latest_start_date: false, has_different_heights: false, has_different_widths: false, has_different_years: false},
        //     {Doc_Width: 14, Doc_Height: 17, DocSizeName: 'Research', Id: 7, DocType: 1, StartDate: startDate, EndDate: endDate, LicensorKey: 206, StatusID: 1, GradSchoolID: 3946, ApplyToGradSchools: false, earliest_start_date: false, latest_start_date: false, has_different_heights: false, has_different_widths: false, has_different_years: false},
        //     {Doc_Width: 14, Doc_Height: 16, DocSizeName: 'PhD', Id: 8, DocType: 1, StartDate: startDate3, EndDate: endDate3, LicensorKey: 206, StatusID: 1, GradSchoolID: 3946, ApplyToGradSchools: false, earliest_start_date: false, latest_start_date: false, has_different_heights: false, has_different_widths: false, has_different_years: false},
        // ];
    }

    // GradSchoolID: number; // int
    // ApplyToGradSchools: boolean; // bit

    // getReplaceImage = () => {
    //     const th6Enabled = !this.prod.th6Disabled && !isNullOrWhiteSpace(this.prod.th6);
    //
    //     if (th6Enabled) {
    //         const defaultConfiguration =
    //             this.prod.MouldingID === this.pcs.MouldingID && this.prod.Mat1_ID === this.pcs.mat1_id && this.prod.Mat2_ID === this.pcs.mat2_id && this.prod.InsigniaID === this.pcs.insignia_id && this.prod.EngravedPlateStatusID === this.pcs.ep_id && this.prod.LinoID === this.pcs.lino_id;
    //
    //         this.replaceImage = defaultConfiguration && th6Enabled && AbTestStore.ABIsVariant(ABTestKeys.ab_prod_th6, 1);
    //     }
    // };
}

const s = new ProdStore();

export default s;
